import { z } from 'zod';

import { timeSeriesDataSchema } from './timeseries-schema';

const themeOverviewResponseSchema = z.object({
  name: z.string(),
  description: z.string(),
  logo_url: z.string().optional(),
});

export type ThemeOverviewResponse = z.infer<typeof themeOverviewResponseSchema>;

export const themePerformanceSchema = z.object({
  index: z.string().datetime(),
  value: z.number(),
});

const entitiesSchema = z.record(
  z.string(),
  z.array(themePerformanceSchema).nullable()
);

export const calculatorResponseSchema = z.object({
  entities: entitiesSchema,
  aggregate: z.array(themePerformanceSchema),
});

export type CalculatorResponse = z.infer<typeof calculatorResponseSchema>;

const performanceReturnsSchema = z.object({
  '1d': z.number(),
  '1w': z.number(),
  '1m': z.number(),
  '1y': z.number(),
});

const getThemePerformanceSchema = z.object({
  data: z.array(
    z.object({
      timestamp: z.iso.datetime(),
      value: z.number(),
    })
  ),
  entities: z.array(z.string()).optional(),
  performance: performanceReturnsSchema.extend({
    '3m': z.number(),
    '6m': z.number(),
  }),
});
export type GetThemePerformance = z.infer<typeof getThemePerformanceSchema>;

export const performanceResponseSchema = z.object({
  themes: z.array(
    performanceReturnsSchema.extend({
      id: z.string(),
    })
  ),
  page: z.number(),
  size: z.number(),
});

export type PerformanceResponse = z.infer<typeof performanceResponseSchema>;

const countrySchema = z.object({
  code: z.string(),
  name: z.string(),
  evidence: z.string(),
});

export const countryExposureSchema = z.object({
  result: z.array(countrySchema),
});

export type CountryExposureResponse = z.infer<typeof countryExposureSchema>;

const relationshipSchema = z.object({
  id: z.string(),
  description: z.string(),
  rank: z.number(),
  rank_change: z.number().nullish(),
  evidence: z.array(z.string()).nullish(),
  source_evidence: z.string().nullish(),
});

const parentSchema = z.object({
  id: z.string(),
  name: z.string(),
  logo_url: z.string(),
});

const themeSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  logo_url: z.string(),
  parent: parentSchema,
});

export const themeDetailsSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  logo_url: z.string(),
  parent_theme: parentSchema,
});

export type ThemeSchemaResponse = z.infer<typeof themeDetailsSchema>;

export const themeExposureSchema = z.object({
  theme: themeSchema,
  relationship: relationshipSchema,
});

export type ThemeExposure = z.infer<typeof themeExposureSchema>;

export const themeExposureResponseSchema = z.array(themeExposureSchema);

export type ThemeExposureResponse = z.infer<typeof themeExposureResponseSchema>;

const themeCompetitorResponseSchema = z.object({
  company: z.string(),
  theme: themeSchema,
  competitors: z.array(z.string()),
});
export type ThemeCompetitorResponse = z.infer<
  typeof themeCompetitorResponseSchema
>;

export const themeCompanySchema = z.object({
  company: z.string(),
  relationship: relationshipSchema,
});
export type ThemeCompany = z.infer<typeof themeCompanySchema>;

const themeCompaniesResponseSchema = z.array(themeCompanySchema);
export type ThemeCompaniesResponse = z.infer<
  typeof themeCompaniesResponseSchema
>;

export const themeChildSchema = z.object({
  theme: themeSchema,
  company_count: z.number(),
  is_company_exposed: z.boolean(),
});
export type ThemeChild = z.infer<typeof themeChildSchema>;

const themeChildrenResponseSchema = z.array(themeChildSchema);
export type ThemeChildrenResponse = z.infer<typeof themeChildrenResponseSchema>;

const companyCompetitorsWithThemesSchema = z.object({
  company: z.string(),
  relationship: relationshipSchema,
  competing_themes: z.array(themeSchema),
});
const companyCompetitorsWithThemesResponseSchema = z.array(
  companyCompetitorsWithThemesSchema
);
export type CompanyCompetitorsWithThemesResponse = z.infer<
  typeof companyCompetitorsWithThemesResponseSchema
>;

const countryExposureRelationshipSchema = z.object({
  id: z.string(),
  description: z.string(),
  rank: z.number(),
  source_evidence: z.string(),
  evidence: z.array(z.string()).optional(),
});

const countryExposureItemSchema = z.object({
  code: z.string(),
  relationship: countryExposureRelationshipSchema,
});

const companyCountryExposureResponseSchema = z.array(countryExposureItemSchema);

export type CompanyCountryExposureItem = z.infer<
  typeof countryExposureItemSchema
>;
export type CompanyCountryExposureResponse = z.infer<
  typeof companyCountryExposureResponseSchema
>;

const regionExposureRelationshipSchema = z.object({
  id: z.string(),
  description: z.string(),
  rank: z.number(),
  source_evidence: z.string(),
  evidence: z.array(z.string()).optional(),
});

const regionExposureItemSchema = z.object({
  name: z.string(),
  icon_url: z.string().url(),
  relationship: regionExposureRelationshipSchema,
});

const companyRegionExposureResponseSchema = z.array(regionExposureItemSchema);

const brandAndProductItemSchema = z.object({
  product: z.string(),
  relationship: relationshipSchema,
});
export type BrandAndProduct = z.infer<typeof brandAndProductItemSchema>;

const brandsAndProductsResponseSchema = z.array(brandAndProductItemSchema);
export type BrandsAndProductsResponse = z.infer<
  typeof brandsAndProductsResponseSchema
>;

export type CompanyRegionExposureItem = z.infer<
  typeof regionExposureItemSchema
>;
export type CompanyRegionExposureResponse = z.infer<
  typeof companyRegionExposureResponseSchema
>;

export const themeOverview = {
  getThemeV2: {
    path: (themeId: string) => `theme/v2/${themeId}`,
    schema: themeDetailsSchema,
  },
  themeCalculator: {
    path: 'calculator/v1/performance',
    schema: calculatorResponseSchema,
  },
  themePerformance: {
    path: 'entity-performance/v1/theme',
    schema: performanceResponseSchema,
  },
  getThemePerformance: {
    path: (themeId: string) => `entity-performance/v1/theme/${themeId}`,
    schema: getThemePerformanceSchema,
  },
  spxBenchmark: {
    path: 'ca/v2/q',
    schema: timeSeriesDataSchema,
  },
  // v3
  getCompanyTheme: {
    path: (entityTag: string, limit?: number) => {
      const limitParam = limit ? `?limit=${limit}` : '';
      return `ontology/v3/company/${entityTag}/theme-exposure${limitParam}`;
    },
    schema: themeExposureResponseSchema,
  },
  getCompanyCompetitors: {
    path: (entityTag: string, themeId: string, limit?: number) => {
      const limitParam = limit ? `?limit=${limit}` : '';
      return `ontology/v3/company/${entityTag}/theme/${themeId}/competitors${limitParam}`;
    },
    schema: themeCompetitorResponseSchema,
  },
  getThemeChildren: {
    path: (themeId: string) => `ontology/v3/theme/${themeId}/children`,
    schema: themeChildrenResponseSchema,
  },
  getThemeCompanies: {
    path: (themeId: string, limit?: number) => {
      const limitParam = limit ? `?limit=${limit}` : '';
      return `ontology/v3/theme/${themeId}/company-exposure${limitParam}`;
    },
    schema: themeCompaniesResponseSchema,
  },
  getCompanyCompetitorsWithThemes: {
    path: (entityTag: string, limit?: number) => {
      const limitParam = limit ? `?limit=${limit}` : '';
      return `ontology/v3/company/${entityTag}/competitors${limitParam}`;
    },
    // Schema to reconfirm once we have the API
    schema: companyCompetitorsWithThemesResponseSchema,
  },
  getCompanyCountryExposure: {
    path: (entityTag: string) =>
      `ontology/v3/company/${entityTag}/country-exposure`,
    schema: companyCountryExposureResponseSchema,
  },
  getCompanyRegionExposure: {
    path: (entityTag: string) =>
      `ontology/v3/company/${entityTag}/region-exposure`,
    schema: companyRegionExposureResponseSchema,
  },
  getBrandsAndProducts: {
    path: (entityTag: string) => `ontology/v3/company/${entityTag}/products`,
    schema: brandsAndProductsResponseSchema,
  },
};
